

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit, Trash2 } from 'lucide-react';
import AdminDeleteDialog from './AdminDeleteDialog';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AdminCardProps } from './types';

const AdminCard = ({ admin, onDelete }: AdminCardProps) => {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  // Handle edit button click
  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Clean the ID by removing the # prefix for URL
    const cleanId = admin.id.replace('#', '');
    console.log(`🔧 Navigating to edit admin: /admins/edit/${cleanId}`);
    navigate(`/admins/edit/${cleanId}`);
  };
  
  // Handle view button click
  const handleView = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Clean the ID by removing the # prefix for URL
    const cleanId = admin.id.replace('#', '');
    console.log(`👁️ Navigating to admin details: /admins/${cleanId}`);
    navigate(`/admins/${cleanId}`);
  };

  // Handle delete button click
  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log(`🗑️ Opening delete dialog for admin: ${admin.id}`);
    setShowDeleteDialog(true);
  };

  // Get initials for avatar fallback
  const getInitials = () => {
    return `${admin.firstName.charAt(0)}${admin.lastName.charAt(0)}`;
  };
  
  // Enhanced 3D effect with mouse tracking - same as dashboard cards
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isHovering) return;
    
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    
    // Calculate mouse position relative to card center
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    // Normalize values (-1 to 1) and limit rotation
    const normalizedX = (x / (rect.width / 2)) * 0.5; // Reduce rotation intensity
    const normalizedY = (y / (rect.height / 2)) * 0.5;
    
    setMousePosition({ x: normalizedX, y: normalizedY });
  };

  return (
    <>
      <Card 
        className="overflow-hidden transition-shadow duration-200 card-3d-container cursor-pointer admin-card"
        style={{
          transform: isHovering ? 
            `perspective(1000px) rotateY(${mousePosition.x * 5}deg) rotateX(${-mousePosition.y * 5}deg) translateZ(15px)` : 
            'perspective(1000px) rotateY(0) rotateX(0) translateZ(0)',
          boxShadow: isHovering ? 
            '0 20px 40px rgba(0, 0, 0, 0.2), 0 0 30px rgba(59, 130, 246, 0.5), 0 0 60px rgba(59, 130, 246, 0.3)' : 
            '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          setIsHovering(false);
          setMousePosition({ x: 0, y: 0 });
        }}
        onClick={handleView}
      >
        <div className="p-4 flex justify-center">
          <Avatar className="h-24 w-24">
            <AvatarImage
              src={admin.profileImage || '/placeholder.svg'}
              alt={`${admin.firstName} ${admin.lastName}`}
              onError={(e) => {
                console.log("Avatar image load error, using fallback");
                e.currentTarget.src = '/placeholder.svg';
              }}
            />
            <AvatarFallback className="text-lg">{getInitials()}</AvatarFallback>
          </Avatar>
        </div>
        
        <CardContent className="p-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-1 truncate">{admin.firstName} {admin.lastName}</h3>
            <p className="text-muted-foreground text-sm mb-2">{admin.position}</p>
            
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {admin.role}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                admin.status === 'Active' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {admin.status}
              </span>
            </div>
          </div>
          
          <div className="space-y-1 text-sm">
            <p className="flex items-start">
              <span className="font-medium w-20">Email:</span>
              <span className="truncate">{admin.email}</span>
            </p>
            <p className="flex items-start">
              <span className="font-medium w-20">Phone:</span>
              <span>{admin.phone}</span>
            </p>
          </div>
        </CardContent>
        
        <CardFooter className="border-t p-4 bg-muted/30">
          <div className="flex justify-between w-full">
            <Button 
              variant="outline" 
              onClick={(e) => {
                e.stopPropagation();
                handleView(e);
              }}
            >
              View
            </Button>
            <div className="space-x-2">
              <Button variant="ghost" size="icon" onClick={handleEdit}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardFooter>
      </Card>

      {/* Delete confirmation dialog */}
      <AdminDeleteDialog 
        admin={admin}
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onDelete={onDelete}
      />
    </>
  );
};

export default AdminCard;
